/**
 * @file    npsSettingsUtils.ts
 * @scope   SHARED
 * @summary Builds and validates the NPS calculation's advanced settings (fixed 0-10 scale with
 *          Detractors 0-6 / Passives 7-8 / Promoters 9-10 thresholds).
 * @consumes FieldModel, NpsSettingsModel, `fieldHasRatings`
 * @produces `buildDefaultNpsSettings`, `isValidNpsSettings`
 * @sideEffects None — pure.
 * @notes Path rule: field has ratings → path = field.path; no ratings → path = 'id'.
 */
import { FieldModel, NpsSettingsModel } from 'models/Enterprise/FieldsManagerModel';
import { fieldHasRatings } from './percentageSettingsUtils';

/**
 * Builds the default NpsSettingsModel for a field when NPS calculation type is first selected.
 * NPS uses a fixed 0-10 scale with default thresholds:
 * - Detractors: 0-6
 * - Passives: 7-8
 * - Promoters: 9-10
 *
 * Rules:
 * - Field has ratings  → path = field.path
 * - Field has no ratings → path = 'id'
 */
export const buildDefaultNpsSettings = (field: FieldModel): NpsSettingsModel => {
  // Standard NPS scale thresholds
  const detractorsMax = 6;
  const promotersMin = 9;

  return {
    detractorsMax,
    promotersMin,
    path: fieldHasRatings(field) ? field.path ?? '' : 'id',
  };
};

/**
 * Validates that detractorsMax < promotersMin
 * and both are within the NPS 0-10 range.
 */
export const isValidNpsSettings = (
  settings: NpsSettingsModel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _field: FieldModel | undefined
): boolean => {
  const minRating = 0;
  const maxRating = 10;

  return (
    settings.detractorsMax >= minRating &&
    settings.detractorsMax < settings.promotersMin &&
    settings.promotersMin <= maxRating
  );
};
